import { useState } from "react";
import type { FormEvent } from "react";
import { hotComments, hotTags } from "@/mocks/homeData";
import SectionHeading from "./SectionHeading";

export default function DiscussionSection() {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  const toggleLike = (index: number) => {
    setLiked((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft.trim()) {
      setNotice("先写点内容再发布吧");
      return;
    }
    setNotice("演示环境：你的评论已在本地预览，未提交到服务器");
    setDraft("");
  };

  return (
    <section id="discussion" className="w-full border-y border-background-200 bg-background-100">
      <div className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="mx-auto w-full max-w-[1220px]">
          <SectionHeading
            index="04"
            label="讨论区"
            title="出处靠大家一起比对"
            desc="热评会被标到对应事件编号旁，时间和平台信息越具体，越容易被采纳。"
          />

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 px-4 py-3">
                <span className="flex items-center gap-1.5 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-chat-smile-2-line text-primary-600"></i>
                  热议出处
                </span>
                <div className="flex items-center gap-3 text-[11px] text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    1,286 条讨论
                  </span>
                  <span className="flex items-center gap-1 text-primary-600">
                    <i className="ri-fire-line"></i>
                    今日热度 9.4
                  </span>
                </div>
              </div>

              <ul className="divide-y divide-background-200">
                {hotComments.map((comment, index) => {
                  const isLiked = Boolean(liked[index]);
                  return (
                    <li key={`${comment.user}-${comment.time}`} className="px-4 py-4">
                      <div className="flex gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 font-heading text-sm font-bold text-background-50">
                          {comment.user.slice(0, 1)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-foreground-900">{comment.user}</span>
                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] text-primary-700">
                              {comment.level}
                            </span>
                            <span className="text-[11px] text-foreground-500">{comment.time}</span>
                          </div>
                          <p className="mt-1.5 text-xs leading-relaxed text-foreground-700 md:text-sm">
                            {comment.text}
                          </p>
                          <div className="mt-2.5 flex flex-wrap items-center gap-4 text-[11px] text-foreground-500">
                            <button
                              type="button"
                              onClick={() => toggleLike(index)}
                              className={`flex items-center gap-1 transition-colors ${
                                isLiked ? "text-primary-600" : "hover:text-primary-600"
                              }`}
                            >
                              <i className={isLiked ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                              {comment.likes + (isLiked ? 1 : 0)}
                            </button>
                            <span className="flex items-center gap-1">
                              <i className="ri-reply-line"></i>
                              回复
                            </span>
                            <a href="#archive" className="flex items-center gap-1 transition-colors hover:text-primary-600">
                              <i className="ri-links-line"></i>
                              {comment.event}
                            </a>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <aside className="space-y-4">
              <form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-600 text-background-50">
                    <i className="ri-quill-pen-line"></i>
                  </span>
                  <span className="font-heading text-sm font-bold text-foreground-950">补充一条出处线索</span>
                </div>
                <textarea
                  name="trace_lead"
                  value={draft}
                  maxLength={500}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={4}
                  placeholder="写下你看到的最早链接、发布时间与平台，例如：H-2418 在短视频平台 A 07:40 首发…"
                  className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-400"
                ></textarea>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500">{draft.length}/500</span>
                  <button
                    type="submit"
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-send-plane-line"></i>
                    发布线索
                  </button>
                </div>
                {notice ? (
                  <p className="mt-2 rounded-md bg-accent-100 px-3 py-2 text-[11px] leading-relaxed text-accent-900">
                    {notice}
                  </p>
                ) : null}
              </form>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="font-heading text-sm font-bold text-foreground-950">讨论热词</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {hotTags.map((tag) => (
                    <a
                      key={tag}
                      href="#archive"
                      className="whitespace-nowrap rounded-full border border-background-300 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
                    >
                      {tag}
                    </a>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="font-heading text-sm font-bold text-foreground-950">讨论氛围</span>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-background-100 px-2 py-3">
                    <div className="font-heading text-lg font-bold text-foreground-950">1,286</div>
                    <div className="mt-0.5 text-[10px] text-foreground-500">条讨论</div>
                  </div>
                  <div className="rounded-md bg-background-100 px-2 py-3">
                    <div className="font-heading text-lg font-bold text-foreground-950">9.4</div>
                    <div className="mt-0.5 text-[10px] text-foreground-500">今日热度</div>
                  </div>
                  <div className="rounded-md bg-background-100 px-2 py-3">
                    <div className="font-heading text-lg font-bold text-foreground-950">312</div>
                    <div className="mt-0.5 text-[10px] text-foreground-500">参与投稿</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}